import { useMemo, useState } from "react";
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import useAuth from "../../../hooks/useAuth";
import {
  fetchOperationsDriverPerformance,
  setOperationsDriverPerformanceComment,
} from "../../../api/operationsDriverPerformanceService";
import {
  OperationsPlannerDriverPerformanceDto,
  OperationsPlannerPerformanceResponseDto,
  PlannerCommentStatusType,
} from "../../../interfaces/Operations/operationsDriverPerformance";
import { operationsWeeklyGrossTargets } from "../../../data/operationsWeeklyGrossTargets";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";

const statusOptions: { label: string; value: PlannerCommentStatusType }[] = [
  { label: "Bad", value: PlannerCommentStatusType.Bad },
  { label: "Neutral", value: PlannerCommentStatusType.Neutral },
  { label: "Good", value: PlannerCommentStatusType.Good },
  { label: "Very good", value: PlannerCommentStatusType.VeryGood },
];

type DraftByDriver = Record<
  string,
  { comment: string; status: PlannerCommentStatusType | "" }
>;

const toMoney = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const startOfWeek = dayjs().startOf("week");

const getTargetCategoryFromTrailerType = (
  trailerType?: string | null,
): keyof (typeof operationsWeeklyGrossTargets)[string] | null => {
  const normalized = (trailerType ?? "").toLowerCase();

  if (!normalized.trim()) return "po";
  if (normalized.includes("rgn")) return "rgn";
  if (normalized.includes("step")) return "step";
  if (normalized.includes("conestoga")) return "conestoga";
  if (normalized.includes("reefer")) return "reefer";
  if (normalized.includes("power only") || normalized.includes("po"))
    return "po";
  if (normalized.includes("flat")) return "flat";
  if (normalized.includes("dry") || normalized.includes("van")) return "dryvan";

  return null;
};

const getStatusColor = (status: PlannerCommentStatusType | "") => {
  switch (status) {
    case PlannerCommentStatusType.Bad:
      return "#ffebee";
    case PlannerCommentStatusType.Neutral:
      return "#fff8e1";
    case PlannerCommentStatusType.Good:
      return "#e8f5e9";
    case PlannerCommentStatusType.VeryGood:
      return "#e3f2fd";
    default:
      return "transparent";
  }
};

const parseStatus = (
  status: PlannerCommentStatusType | string | null | undefined,
): PlannerCommentStatusType | "" => {
  if (status === null || status === undefined) return "";

  if (typeof status === "number") {
    return status;
  }

  const normalized = status.toString().toLowerCase().replace(/\s+|_/g, "");

  switch (normalized) {
    case "bad":
      return PlannerCommentStatusType.Bad;
    case "neutral":
      return PlannerCommentStatusType.Neutral;
    case "good":
      return PlannerCommentStatusType.Good;
    case "verygood":
      return PlannerCommentStatusType.VeryGood;
    default:
      return "";
  }
};

const OperationsDriverPerformance = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState<Dayjs | null>(startOfWeek);
  const [endDate, setEndDate] = useState<Dayjs | null>(
    startOfWeek.add(6, "day"),
  );
  const [draftByDriver, setDraftByDriver] = useState<DraftByDriver>({});

  const startDateKey = startDate?.format("YYYY-MM-DD");
  const endDateKey = endDate?.format("YYYY-MM-DD");

  const {
    data: performance,
    isLoading,
    error,
    refetch,
  } = useQuery<OperationsPlannerPerformanceResponseDto, Error>({
    queryKey: ["operations-driver-performance", startDateKey, endDateKey],
    queryFn: () =>
      fetchOperationsDriverPerformance(token, startDateKey, endDateKey),
    enabled: Boolean(token && startDateKey && endDateKey),
  });

  const saveCommentMutation = useMutation({
    mutationFn: ({
      driverId,
      comment,
      status,
    }: {
      driverId: string;
      comment: string;
      status: PlannerCommentStatusType | "";
    }) =>
      setOperationsDriverPerformanceComment(token, {
        driverId,
        startDate: startDateKey ?? null,
        endDate: endDateKey ?? null,
        comment: comment.trim() ? comment.trim() : null,
        status: status === "" ? null : status,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["operations-driver-performance", startDateKey, endDateKey],
      });
    },
  });

  const mergedDriver = (
    driver: OperationsPlannerDriverPerformanceDto,
  ): { comment: string; status: PlannerCommentStatusType | "" } => {
    const draft = draftByDriver[driver.driverId];

    if (draft) {
      return draft;
    }

    return {
      comment: driver.comment ?? "",
      status: parseStatus(driver.status),
    };
  };

  const totals = useMemo(() => {
    const drivers =
      performance?.dispatchers.flatMap((dispatcher) => dispatcher.drivers) ??
      [];

    return {
      gross: drivers.reduce((acc, current) => acc + current.gross, 0),
      averageRpm: drivers.length
        ? drivers.reduce((acc, current) => acc + current.rpm, 0) /
          drivers.length
        : 0,
    };
  }, [performance]);

  const targetsByDriverId = useMemo(() => {
    const monthDate = startDate ?? dayjs();
    const monthKey = monthDate.format("YYYY-MM");
    const weeklyTargets = operationsWeeklyGrossTargets[monthKey] ?? {};

    const allDrivers =
      performance?.dispatchers.flatMap((dispatcher) => dispatcher.drivers) ??
      [];

    return allDrivers.reduce<
      Record<string, { targetGross: number | null; targetRpm: number | null }>
    >((acc, driver) => {
      const category = getTargetCategoryFromTrailerType(driver.trailerType);
      const weeklyTarget = category ? weeklyTargets[category] : undefined;

      acc[driver.driverId] = {
        targetGross: weeklyTarget?.gross ?? null,
        targetRpm: weeklyTarget?.rpm ?? null,
      };

      return acc;
    }, {});
  }, [performance, startDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h4">Driver Performance</Typography>

        <Card>
          <CardHeader title="Filters" />
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              <DatePicker
                label="Start date"
                value={startDate}
                onChange={(value) =>
                  setStartDate(value ? value.startOf("day") : null)
                }
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End date"
                value={endDate}
                onChange={(value) =>
                  setEndDate(value ? value.startOf("day") : null)
                }
                minDate={startDate ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <Button variant="contained" onClick={() => refetch()}>
                Refresh
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Total gross" />
                <CardContent>
                  <Typography variant="h5">${toMoney(totals.gross)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Average RPM" />
                <CardContent>
                  <Typography variant="h5">
                    {totals.averageRpm.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardHeader title="Date range" />
                <CardContent>
                  <Typography>
                    {performance?.startDate
                      ? dayjs(performance.startDate).format("MMM D, YYYY")
                      : "-"}{" "}
                    -{" "}
                    {performance?.endDate
                      ? dayjs(performance.endDate).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {isLoading && <CircularProgress />}
        {error && <Alert severity="error">{error.message}</Alert>}

        {(performance?.dispatchers ?? []).map((dispatcher) => (
          <Card key={dispatcher.dispatcherId}>
            <CardHeader
              title={dispatcher.dispatcherNickname || "Dispatcher"}
              subheader={
                dispatcher.dispatcherWorkPhoneNumber || "No work phone"
              }
            />
            <Divider />
            <CardContent>
              <TableContainer
                role="region"
                aria-label="Driver performance table"
                tabIndex={0}
              >
                <Table sx={{ minWidth: 1100 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Driver</TableCell>
                      <TableCell>Trailer type</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="right">RPM</TableCell>
                      <TableCell align="right">Target gross</TableCell>
                      <TableCell align="right">Target RPM</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dispatcher.drivers.map((driver) => {
                      const merged = mergedDriver(driver);
                      const target = targetsByDriverId[driver.driverId];

                      return (
                        <TableRow key={driver.driverId}>
                          <TableCell>{driver.driverName || "-"}</TableCell>
                          <TableCell>
                            {camelCaseToSentenceCase(
                              driver.trailerType ?? "PO",
                            )}
                          </TableCell>
                          <TableCell align="right">
                            ${toMoney(driver.gross)}
                          </TableCell>
                          <TableCell align="right">
                            {driver.rpm.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {target?.targetGross !== null &&
                            target?.targetGross !== undefined
                              ? `$${toMoney(target.targetGross)}`
                              : "-"}
                          </TableCell>
                          <TableCell align="right">
                            {target?.targetRpm !== null &&
                            target?.targetRpm !== undefined
                              ? target.targetRpm.toFixed(2)
                              : "-"}
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={merged.status}
                              onChange={(event) => {
                                const value = event.target.value;

                                setDraftByDriver((prev) => ({
                                  ...prev,
                                  [driver.driverId]: {
                                    ...merged,
                                    status:
                                      value === ""
                                        ? ""
                                        : (Number(
                                            value,
                                          ) as PlannerCommentStatusType),
                                  },
                                }));
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: getStatusColor(
                                    merged.status,
                                  ),
                                },
                              }}
                            >
                              <MenuItem value="">Not set</MenuItem>
                              {statusOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell sx={{ minWidth: 260 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={merged.comment}
                              onChange={(event) => {
                                const value = event.target.value;

                                setDraftByDriver((prev) => ({
                                  ...prev,
                                  [driver.driverId]: {
                                    ...merged,
                                    comment: value,
                                  },
                                }));
                              }}
                              placeholder="Leave comment"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              onClick={() =>
                                saveCommentMutation.mutate({
                                  driverId: driver.driverId,
                                  comment: merged.comment,
                                  status: merged.status,
                                })
                              }
                            >
                              Save
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </LocalizationProvider>
  );
};

export default OperationsDriverPerformance;
